// Small curated reference library for the wizard.
// All locally-stored PHI-free pick-lists to reduce RN typing.
//
// Diagnoses + medications now flow through the per-agency Mongo catalog
// (`/api/catalog/diagnoses`, `/api/catalog/medications`). When the
// browser is offline we fall back to the cached seed list below so
// rural-home autocomplete still works. Each successful API search
// refreshes the cache.

import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CACHE_KEY_DX = "wi-pa-catalog-dx-v1";
const CACHE_KEY_MED = "wi-pa-catalog-med-v1";

// ---------- Hard-coded offline seed (legacy fallback only) ----------
// Kept so the wizard still renders SOMETHING if the user is offline AND
// the catalog cache is empty (first-ever load with no network). The
// real catalog (~595 dx, ~285 meds) lives server-side in MongoDB.
export const COMMON_DIAGNOSES = [
  { icd10: "I50.9",  description: "Heart failure, unspecified" },
  { icd10: "I10",    description: "Essential (primary) hypertension" },
  { icd10: "E11.9",  description: "Type 2 diabetes mellitus without complications" },
  { icd10: "N18.9",  description: "Chronic kidney disease, unspecified" },
  { icd10: "J44.9",  description: "COPD, unspecified" },
  { icd10: "G30.9",  description: "Alzheimer's disease, unspecified" },
  { icd10: "F03.90", description: "Unspecified dementia without behavioral disturbance" },
  { icd10: "G20",    description: "Parkinson's disease" },
  { icd10: "M17.11", description: "Osteoarthritis of right knee" },
  { icd10: "I63.9",  description: "Cerebral infarction, unspecified (CVA)" },
  { icd10: "F33.1",  description: "Major depressive disorder, recurrent, moderate" },
  { icd10: "F41.1",  description: "Generalized anxiety disorder" },
  { icd10: "Z74.1",  description: "Need for assistance with personal care" },
  { icd10: "Z99.3",  description: "Dependence on wheelchair" },
  { icd10: "R29.6",  description: "Repeated falls" },
];

export const COMMON_MEDICATIONS = [
  { name: "Metformin",      dose: "500 mg",   frequency: "BID",    route: "PO", syn: ["glucophage"] },
  { name: "Lisinopril",     dose: "10 mg",    frequency: "daily",  route: "PO", syn: ["prinivil","zestril"] },
  { name: "Atorvastatin",   dose: "20 mg",    frequency: "nightly",route: "PO", syn: ["lipitor"] },
  { name: "Amlodipine",     dose: "5 mg",     frequency: "daily",  route: "PO", syn: ["norvasc"] },
  { name: "Levothyroxine",  dose: "50 mcg",   frequency: "daily",  route: "PO", syn: ["synthroid"] },
  { name: "Furosemide",     dose: "40 mg",    frequency: "daily",  route: "PO", syn: ["lasix"] },
  { name: "Aspirin",        dose: "81 mg",    frequency: "daily",  route: "PO", syn: [] },
  { name: "Apixaban",       dose: "5 mg",     frequency: "BID",    route: "PO", syn: ["eliquis"] },
];

// ---------- Cache helpers ----------
function readCache(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeCache(key, items) {
  try {
    localStorage.setItem(key, JSON.stringify(items.slice(0, 500)));
  } catch {
    /* localStorage quota exceeded — non-fatal, search just hits API next time */
  }
}

// ---------- Async API-backed search ----------
// `searchDiagnosesAsync` and `searchMedicationsAsync` return a promise of
// the merged seed + agency catalog from MongoDB. They cache the most
// recent successful response per query in localStorage so the wizard
// stays usable when the parent's network drops on the way to the home.

export async function searchDiagnosesAsync(q, agencyId) {
  const params = new URLSearchParams();
  params.set("q", q || "");
  if (agencyId) params.set("agency_id", agencyId);
  params.set("limit", "25");
  try {
    const r = await axios.get(`${API}/catalog/diagnoses?${params.toString()}`, { timeout: 8000 });
    const items = (r.data?.items || []).map((d) => ({
      icd10: d.icd10 || "",
      description: d.description || "",
      source: d.source,
      usage_count: d.usage_count || 0,
    }));
    if (!q) writeCache(CACHE_KEY_DX, items);
    return items;
  } catch {
    // Network down — fall back to cache, then the hard-coded seed.
    const cached = readCache(CACHE_KEY_DX);
    const fallback = (cached.length ? cached : COMMON_DIAGNOSES).slice();
    if (!q) return fallback.slice(0, 25);
    const s = q.toLowerCase();
    return fallback
      .filter((d) => (d.icd10 || "").toLowerCase().includes(s) || (d.description || "").toLowerCase().includes(s))
      .slice(0, 25);
  }
}

export async function searchMedicationsAsync(q, agencyId) {
  const params = new URLSearchParams();
  params.set("q", q || "");
  if (agencyId) params.set("agency_id", agencyId);
  params.set("limit", "25");
  try {
    const r = await axios.get(`${API}/catalog/medications?${params.toString()}`, { timeout: 8000 });
    const items = (r.data?.items || []).map((m) => ({
      name: m.name || "",
      dose: m.dose || "",
      frequency: m.frequency || "",
      route: m.route || "",
      syn: m.syn || [],
      source: m.source,
      usage_count: m.usage_count || 0,
    }));
    if (!q) writeCache(CACHE_KEY_MED, items);
    return items;
  } catch {
    const cached = readCache(CACHE_KEY_MED);
    const fallback = (cached.length ? cached : COMMON_MEDICATIONS).slice();
    if (!q) return fallback.slice(0, 12);
    const s = q.toLowerCase();
    return fallback
      .filter(
        (m) =>
          (m.name || "").toLowerCase().includes(s) ||
          (m.syn || []).some((x) => (x || "").toLowerCase().includes(s)),
      )
      .slice(0, 25);
  }
}

// ---------- POST helpers — register the user's new entry in catalog ----------

export async function registerDiagnosis({ icd10, description, agency_id, added_by }) {
  if (!description?.trim()) return null;
  try {
    const r = await axios.post(`${API}/catalog/diagnoses`, {
      icd10: (icd10 || "").trim(),
      description: description.trim(),
      agency_id: agency_id || null,
      added_by: added_by || "",
    }, { timeout: 8000 });
    return r.data?.item || null;
  } catch {
    /* offline — entry stays only in this submission's payload; will be
       picked up next online session via duplication tolerance */
    return null;
  }
}

export async function registerMedication({ name, dose, frequency, route, agency_id, added_by }) {
  if (!name?.trim()) return null;
  try {
    const r = await axios.post(`${API}/catalog/medications`, {
      name: name.trim(),
      dose: (dose || "").trim(),
      frequency: (frequency || "").trim(),
      route: (route || "").trim(),
      agency_id: agency_id || null,
      added_by: added_by || "",
    }, { timeout: 8000 });
    return r.data?.item || null;
  } catch {
    return null;
  }
}

// ---------- Sync helpers (legacy callers — synchronous local-only search) ----------
// Kept so any older import sites still build; new code should use the
// async variants. These hit the hard-coded seed only.
export function searchDiagnoses(q) {
  const s = (q || "").trim().toLowerCase();
  if (!s) return COMMON_DIAGNOSES.slice(0, 20);
  return COMMON_DIAGNOSES.filter(
    (d) => d.icd10.toLowerCase().includes(s) || d.description.toLowerCase().includes(s),
  ).slice(0, 20);
}

export function searchMedications(q) {
  const s = (q || "").trim().toLowerCase();
  if (!s) return COMMON_MEDICATIONS.slice(0, 12);
  return COMMON_MEDICATIONS.filter(
    (m) =>
      m.name.toLowerCase().includes(s) ||
      (m.syn || []).some((x) => x.toLowerCase().includes(s)),
  ).slice(0, 12);
}

// ADL quick-patterns — sets pcst_level + score + sensible days/week for all six ADLs in one tap.
// PCST level vocabulary per F-11133 uses single-letter codes (A–F / A–G for grooming / 0–E for eating).
// Each ADL has its own option set in sampleData.PCST_ADL_LEVELS — the mapping below targets
// the closest letter per pattern for each ADL so the dropdown actually updates.
//
// Score field uses the 0-4 OASIS-style short codes.
export const ADL_PATTERNS = [
  {
    id: "independent",
    label: "Fully Independent",
    blurb: "Client performs all ADLs without assistance.",
    emoji: "🟢",
    values: {
      bathing:      { pcst_level: "A", score: "0", days_per_week: "7" },
      dressing:     { pcst_level: "A", score: "0", days_per_week: "7" },
      eating:       { pcst_level: "A", score: "0", days_per_week: "7" },
      mobility:     { pcst_level: "A", score: "0", days_per_week: "7" },
      toileting:    { pcst_level: "A", score: "0", days_per_week: "7" },
      transferring: { pcst_level: "A", score: "0", days_per_week: "7" },
    },
  },
  {
    id: "setup",
    label: "Setup / Cueing Only",
    blurb: "Needs items set up or verbal cues; otherwise independent.",
    emoji: "🟡",
    values: {
      bathing:      { pcst_level: "B", score: "1", days_per_week: "7" },
      dressing:     { pcst_level: "B", score: "1", days_per_week: "7" },
      eating:       { pcst_level: "B", score: "1", days_per_week: "7" },
      mobility:     { pcst_level: "B", score: "1", days_per_week: "7" },
      toileting:    { pcst_level: "B", score: "1", days_per_week: "7" },
      transferring: { pcst_level: "B", score: "1", days_per_week: "7" },
    },
  },
  {
    id: "moderate",
    label: "Moderate Assistance",
    blurb: "Needs physical help part of each task across all ADLs.",
    emoji: "🟠",
    values: {
      bathing:      { pcst_level: "D", score: "2", days_per_week: "7" },
      dressing:     { pcst_level: "D", score: "2", days_per_week: "7" },
      eating:       { pcst_level: "F", score: "2", days_per_week: "7" },
      mobility:     { pcst_level: "D", score: "2", days_per_week: "7" },
      toileting:    { pcst_level: "D", score: "2", days_per_week: "7" },
      transferring: { pcst_level: "D", score: "2", days_per_week: "7" },
    },
  },
  {
    id: "total",
    label: "Total Care",
    blurb: "Full dependence across all ADLs.",
    emoji: "🔴",
    values: {
      bathing:      { pcst_level: "E", score: "4", days_per_week: "7" },
      dressing:     { pcst_level: "E", score: "4", days_per_week: "7" },
      eating:       { pcst_level: "G", score: "4", days_per_week: "7" },
      mobility:     { pcst_level: "D", score: "4", days_per_week: "7" },
      toileting:    { pcst_level: "E", score: "4", days_per_week: "7" },
      transferring: { pcst_level: "E", score: "4", days_per_week: "7" },
    },
  },
];
